import type { ForecastDay } from '../types/weather';

export type WeatherAlertType =
  | 'thunderstorm'
  | 'extremeHeat'
  | 'extremeCold'
  | 'freezingRain'
  | 'heavyRain';

export type AlertSeverity = 'moderate' | 'severe';

export interface WeatherAlert {
  type: WeatherAlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
}

/** Open-Meteo returns 0–100; iOS thresholds use 0–1 fractions. */
export function normalizePrecipFraction(value: number): number {
  if (value > 1) {
    return value / 100;
  }
  return value;
}

function isThunderstormCondition(condition: string): boolean {
  return condition.toLowerCase().includes('thunder');
}

export function detectWeatherAlerts(day: ForecastDay | null): WeatherAlert[] {
  if (!day || day.isHistorical) {
    return [];
  }

  const alerts: WeatherAlert[] = [];
  const precipFraction = normalizePrecipFraction(day.precipitationProbability);

  if (isThunderstormCondition(day.condition)) {
    alerts.push({
      type: 'thunderstorm',
      severity: 'severe',
      title: 'Thunderstorm',
      message: 'Thunderstorms are forecast for this day. Plan indoor alternatives.',
    });
  }

  if (day.highTempC > 40) {
    alerts.push({
      type: 'extremeHeat',
      severity: 'severe',
      title: 'Extreme heat',
      message: 'Very high temperatures expected. Stay hydrated and limit outdoor exposure.',
    });
  } else if (day.highTempC > 35) {
    alerts.push({
      type: 'extremeHeat',
      severity: 'moderate',
      title: 'High heat',
      message: 'Hot weather expected. Drink water and seek shade during peak hours.',
    });
  }

  if (day.lowTempC < -10) {
    alerts.push({
      type: 'extremeCold',
      severity: 'severe',
      title: 'Extreme cold',
      message: 'Very cold temperatures expected. Dress in layers and limit time outdoors.',
    });
  }

  if (day.lowTempC < 0 && precipFraction > 0.3) {
    alerts.push({
      type: 'freezingRain',
      severity: 'moderate',
      title: 'Freezing conditions',
      message: 'Below-freezing temperatures with precipitation. Watch for icy surfaces.',
    });
  }

  if (precipFraction > 0.8) {
    alerts.push({
      type: 'heavyRain',
      severity: 'moderate',
      title: 'Heavy precipitation',
      message: 'High chance of rain or snow. Bring weather-appropriate gear.',
    });
  }

  return alerts;
}
